<template>
  <div id="topbar-container">
    <div id="topbar-left">
      <div id="main-nav">
        <router-link
            v-slot="{ href, route, navigate, isActive, isExactActive }"
            :to="{ name: 'JobCanvas', params: { uuid: id } }"
        >
          <div
              :class="[isActive && 'active', isExactActive && 'exact-active']"
              class="nav-item"
              @click="navigate"
          >
            <svg height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
              <path
                  d="M20 11V9a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h4m4 5v-4a2 2 0 1 1 4 0v4m-4-2h4m3-4v6"
                  fill="none"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
              />
            </svg>
            {{ 'Create' | trans }}
          </div>
        </router-link>
        <router-link
            v-slot="{ href, route, navigate, isActive, isExactActive }"
            :to="{ name: 'StyleCanvas', params: { uuid: id } }"
        >
          <div
              :class="[isActive && 'active', isExactActive && 'exact-active']"
              class="nav-item d-flex d-inline-flex align-items-center h-100 gap-50"
              @click="navigate"
          >
            <i class="far fa-style lh-inherit font-size-125"></i>
            {{ 'Styles' | trans }}
          </div>
        </router-link>
        <router-link
            v-slot="{ href, route, navigate, isActive, isExactActive }"
            :to="{ name: 'PromptEditorCanvas', params: { uuid: id } }"
        >
          <div
              :class="[isActive && 'active', isExactActive && 'exact-active']"
              class="nav-item d-flex d-inline-flex align-items-center h-100 gap-50"
              @click="navigate"
          >
            <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                  d="M1.33325 0.666626L4.66659 3.99996L1.33325 7.33329M6.66659 7.33329H10.6666"
                  stroke="white"
                  stroke-width="1.33333"
                  stroke-linecap="round"
                  stroke-linejoin="round"
              />
            </svg>
            {{ 'Prompt Editor' | trans }}
          </div>
        </router-link>
      </div>

      <div id="files-bar-container" class="open-files-nav flex-1 overflow-x-auto no-scrollbar" @wheel="onWheel">
        <router-link
            v-for="(job, index) in openJobs"
            :key="index"
            v-slot="{ href, route, navigate, isActive, isExactActive }"
            :to="{ name: 'JobCanvasIndex', params: { uuid: job.id } }"
        >
          <div
              :class="[isActive && 'active', isExactActive && 'exact-active']"
              class="nav-item-file"
              @click="openJob(job.id)"
              @click.middle="remove(job.id, index, isActive)"
          >
            <i v-if="job.state !== 'processing'" class="far fa-image lh-inherit"></i>
            <i v-if="job.state === 'processing'" class="fa fa-spin btn-loading-animation lh-inherit"></i>
            <div v-if="job.settings.prompt !== '' && job.settings.activeTool !== 'similiarimage'" class="file-text">
              {{ job.settings.prompt }}
            </div>
            <div v-if="job.settings.activeTool === 'similiarimage' && job.settings.similiarImageJob.apiJob !== null"
                 class="file-text">{{ job.settings.similiarImageJob.apiJob?.input[0].filename }}
            </div>
            <div v-if="job.settings.prompt === '' && job.settings.similiarImageJob.apiJob === null" class="file-text">
              {{ 'Draft' | trans }}
            </div>
            <i
                class="far fa-times lh-inherit"
                @click.stop="remove(job.id, index, isActive)"
            ></i>
          </div>
        </router-link>

        <div class="nav-item-file nav-item-file-new" @click="createJob">
          <i class="fa fa-plus lh-inherit"></i> {{ 'New' | trans }}
        </div>
        <div class="nav-item-file nav-item-file-empty"></div>
      </div>

      <div class="topbar-scroll-container" v-if="openJobs?.length > 2">
        <i class="lh-inherit fa fa-chevron-left" @click="scrollLeft"></i>
        <i class="lh-inherit fa fa-chevron-right" @click="scrollRight"></i>
      </div>
    </div>
    <div id="topbar-right">
      <div class="d-flex justify-content-between h-100">
        <router-link
            v-slot="{ href, route, navigate, isActive, isExactActive }"
            :to="{ name: 'CreationsHistory', params: { uuid: id } }"
        >
          <div
              :class="[isActive && 'active', isExactActive && 'exact-active']"
              class="nav-item d-flex d-inline-flex align-items-center h-100 gap-50"
              @click="navigate"
          >
            <i class="far fa-images"></i>
            {{ 'Creations' | trans }}
          </div>
        </router-link>
        <router-link
            v-slot="{ href, route, navigate, isActive, isExactActive }"
            :to="{ name: 'SettingCanvas', params: { uuid: id } }"
        >
          <div
              :class="[isActive && 'active', isExactActive && 'exact-active']"
              class="nav-item d-flex d-inline-flex align-items-center h-100 gap-50"
              @click="navigate"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="14" viewBox="0 0 15 14" fill="none">
              <path
                  d="M6.87683 2.39604C6.53328 2.42066 6.18858 2.36819 5.86792 2.24246C5.54726 2.11673 5.25876 1.92093 5.0235 1.66937L4.9675 1.66871C4.73216 1.92036 4.44355 2.11621 4.12277 2.24194C3.80198 2.36767 3.45715 2.42009 3.1135 2.39537C3.0612 2.74339 2.93035 3.07493 2.73087 3.36486C2.53139 3.65479 2.26851 3.89549 1.96216 4.06871C2.09122 4.36224 2.15786 4.67939 2.15786 5.00004C2.15786 5.32069 2.09122 5.63784 1.96216 5.93137C2.26851 6.10459 2.53139 6.34529 2.73087 6.63522C2.93035 6.92515 3.0612 7.25669 3.1135 7.60471C3.45704 7.58009 3.80174 7.63256 4.1224 7.75829C4.44306 7.88402 4.73157 8.07981 4.96683 8.33137H5.02283C5.25809 8.07981 5.5466 7.88402 5.86726 7.75829C6.18791 7.63256 6.53262 7.58009 6.87616 7.60471C6.92846 7.25669 7.05931 6.92515 7.25879 6.63522C7.45827 6.34529 7.72115 6.10459 8.0275 5.93137C7.89844 5.63784 7.8318 5.32069 7.8318 5.00004C7.8318 4.67939 7.89844 4.36224 8.0275 4.06871C7.72128 3.89541 7.45853 3.65468 7.25916 3.36476C7.0598 3.07484 6.92905 2.74333 6.87683 2.39537V2.39604ZM9.66683 4.14271C9.51445 4.22806 9.38756 4.35249 9.29925 4.50318C9.21094 4.65387 9.16438 4.82538 9.16438 5.00004C9.16438 5.1747 9.21094 5.34621 9.29925 5.4969C9.38756 5.64759 9.51445 5.77202 9.66683 5.85737L9.33216 6.98004C9.15131 6.94879 8.96531 6.96885 8.79528 7.03794C8.62525 7.10703 8.47798 7.22239 8.37018 7.37093C8.26238 7.51948 8.19837 7.69526 8.1854 7.87833C8.17244 8.06141 8.21104 8.24446 8.29683 8.40671L7.33483 9.15471C7.20742 9.05031 7.05579 8.97964 6.8939 8.9492C6.73202 8.91876 6.56508 8.92953 6.40844 8.98052C6.25181 9.03152 6.11053 9.12109 5.99759 9.241C5.88465 9.36091 5.80369 9.50731 5.76216 9.66671L4.22616 9.66271C4.18404 9.50428 4.10292 9.35892 3.9902 9.23989C3.87749 9.12085 3.73677 9.03192 3.58088 8.98122C3.42499 8.93051 3.25888 8.91964 3.09771 8.9496C2.93654 8.97955 2.78543 9.04938 2.65816 9.15271L1.69683 8.39871C1.78032 8.23709 1.81728 8.05546 1.80359 7.87407C1.7899 7.69268 1.72609 7.51865 1.61928 7.3714C1.51247 7.22415 1.36686 7.10946 1.19869 7.04012C1.03051 6.97079 0.846382 6.94952 0.666829 6.97871L0.333496 5.85204C0.483187 5.76588 0.607525 5.64179 0.693982 5.49227C0.780439 5.34275 0.825961 5.17309 0.825961 5.00037C0.825961 4.82766 0.780439 4.65799 0.693982 4.50848C0.607525 4.35896 0.483187 4.23487 0.333496 4.14871L0.666829 3.02204C0.846382 3.05123 1.03051 3.02996 1.19869 2.96062C1.36686 2.89129 1.51247 2.7766 1.61928 2.62935C1.72609 2.4821 1.7899 2.30807 1.80359 2.12668C1.81728 1.94529 1.78032 1.76366 1.69683 1.60204L2.65816 0.846707C2.78543 0.950192 2.93659 1.02015 3.09785 1.05019C3.25911 1.08023 3.42532 1.0694 3.58131 1.01868C3.73731 0.967963 3.87811 0.878979 3.99087 0.759852C4.10363 0.640724 4.18475 0.495249 4.22683 0.336707L5.76216 0.333374C5.80374 0.49266 5.8847 0.638936 5.99759 0.758755C6.11048 0.878573 6.25168 0.968089 6.40821 1.01907C6.56474 1.07005 6.73158 1.08086 6.89338 1.05051C7.05518 1.02016 7.20675 0.949619 7.33416 0.845374L8.29683 1.59337C8.21104 1.75563 8.17244 1.93867 8.1854 2.12175C8.19837 2.30483 8.26238 2.48061 8.37018 2.62915C8.47798 2.77769 8.62525 2.89305 8.79528 2.96214C8.96531 3.03123 9.15131 3.05129 9.33216 3.02004L9.66683 4.14271ZM5.79216 10.2747C6.33467 10.1943 6.86139 10.0301 7.3535 9.78804C7.33577 9.83645 7.31642 9.88426 7.2955 9.93137C7.60184 10.1046 7.86472 10.3453 8.0642 10.6352C8.26369 10.9251 8.39454 11.2567 8.44683 11.6047C8.79037 11.5801 9.13508 11.6326 9.45574 11.7583C9.7764 11.884 10.0649 12.0798 10.3002 12.3314H10.3562C10.5914 12.0798 10.8799 11.884 11.2006 11.7583C11.5212 11.6326 11.866 11.5801 12.2095 11.6047C12.2618 11.2567 12.3926 10.9251 12.5921 10.6352C12.7916 10.3453 13.0545 10.1046 13.3608 9.93137C13.2318 9.63784 13.1651 9.32069 13.1651 9.00004C13.1651 8.67939 13.2318 8.36224 13.3608 8.06871C13.0546 7.89541 12.7919 7.65468 12.5925 7.36476C12.3931 7.07484 12.2624 6.74333 12.2102 6.39537C11.8666 6.41999 11.5219 6.36752 11.2013 6.24179C10.8806 6.11607 10.5921 5.92027 10.3568 5.66871H10.3008L10.2908 5.67937C10.3474 5.23319 10.3478 4.78167 10.2922 4.33537L11.0962 4.33337C11.1377 4.49266 11.2187 4.63894 11.3316 4.75875C11.4445 4.87857 11.5857 4.96809 11.7422 5.01907C11.8987 5.07005 12.0656 5.08086 12.2274 5.05051C12.3892 5.02016 12.5408 4.94962 12.6682 4.84537L13.6308 5.59337C13.545 5.75563 13.5064 5.93867 13.5194 6.12175C13.5324 6.30483 13.5964 6.48061 13.7042 6.62915C13.812 6.77769 13.9592 6.89305 14.1293 6.96214C14.2993 7.03123 14.4853 7.05129 14.6662 7.02004L15.0002 8.14271C14.8478 8.22806 14.7209 8.35249 14.6326 8.50318C14.5443 8.65387 14.4977 8.82538 14.4977 9.00004C14.4977 9.1747 14.5443 9.34621 14.6326 9.4969C14.7209 9.64759 14.8478 9.77202 15.0002 9.85737L14.6655 10.98C14.4846 10.9488 14.2986 10.9688 14.1286 11.0379C13.9586 11.107 13.8113 11.2224 13.7035 11.3709C13.5957 11.5195 13.5317 11.6953 13.5187 11.8783C13.5058 12.0614 13.5444 12.2445 13.6302 12.4067L12.6682 13.1547C12.5408 13.0503 12.3891 12.9796 12.2272 12.9492C12.0654 12.9188 11.8984 12.9295 11.7418 12.9805C11.5851 13.0315 11.4439 13.1211 11.3309 13.241C11.218 13.3609 11.137 13.5073 11.0955 13.6667L9.5595 13.6627C9.51737 13.5043 9.43625 13.3589 9.32354 13.2399C9.21083 13.1209 9.07011 13.0319 8.91421 12.9812C8.75832 12.9305 8.59221 12.9196 8.43104 12.9496C8.26987 12.9796 8.11876 13.0494 7.9915 13.1527L7.03016 12.3987C7.11366 12.2371 7.15062 12.0555 7.13692 11.8741C7.12323 11.6927 7.05942 11.5187 6.95261 11.3714C6.84581 11.2242 6.7002 11.1095 6.53202 11.0401C6.36384 10.9708 6.17972 10.9495 6.00016 10.9787L5.79216 10.2747ZM5.00016 7.00004C4.73752 7.00004 4.47745 6.94831 4.2348 6.8478C3.99214 6.74729 3.77167 6.59997 3.58595 6.41425C3.40023 6.22854 3.25291 6.00806 3.1524 5.76541C3.05189 5.52276 3.00016 5.26268 3.00016 5.00004C3.00016 4.7374 3.05189 4.47732 3.1524 4.23467C3.25291 3.99202 3.40023 3.77154 3.58595 3.58583C3.77167 3.40011 3.99214 3.25279 4.2348 3.15228C4.47745 3.05177 4.73752 3.00004 5.00016 3.00004C5.5306 3.00004 6.0393 3.21075 6.41438 3.58583C6.78945 3.9609 7.00016 4.46961 7.00016 5.00004C7.00016 5.53047 6.78945 6.03918 6.41438 6.41425C6.0393 6.78933 5.5306 7.00004 5.00016 7.00004ZM5.00016 5.66671C5.17697 5.66671 5.34654 5.59647 5.47157 5.47145C5.59659 5.34642 5.66683 5.17685 5.66683 5.00004C5.66683 4.82323 5.59659 4.65366 5.47157 4.52864C5.34654 4.40361 5.17697 4.33337 5.00016 4.33337C4.82335 4.33337 4.65378 4.40361 4.52876 4.52864C4.40373 4.65366 4.3335 4.82323 4.3335 5.00004C4.3335 5.17685 4.40373 5.34642 4.52876 5.47145C4.65378 5.59647 4.82335 5.66671 5.00016 5.66671ZM10.3335 11C9.80306 11 9.29436 10.7893 8.91928 10.4143C8.54421 10.0392 8.3335 9.53047 8.3335 9.00004C8.3335 8.46961 8.54421 7.9609 8.91928 7.58583C9.29436 7.21075 9.80306 7.00004 10.3335 7.00004C10.8639 7.00004 11.3726 7.21075 11.7477 7.58583C12.1228 7.9609 12.3335 8.46961 12.3335 9.00004C12.3335 9.53047 12.1228 10.0392 11.7477 10.4143C11.3726 10.7893 10.8639 11 10.3335 11ZM10.3335 9.66671C10.5103 9.66671 10.6799 9.59647 10.8049 9.47145C10.9299 9.34642 11.0002 9.17685 11.0002 9.00004C11.0002 8.82323 10.9299 8.65366 10.8049 8.52864C10.6799 8.40361 10.5103 8.33337 10.3335 8.33337C10.1567 8.33337 9.98712 8.40361 9.86209 8.52864C9.73707 8.65366 9.66683 8.82323 9.66683 9.00004C9.66683 9.17685 9.73707 9.34642 9.86209 9.47145C9.98712 9.59647 10.1567 9.66671 10.3335 9.66671Z"
                  fill="white"
              />
            </svg>
            {{ 'Advanced Settings' | trans }}
          </div>
        </router-link>
        <div class="d-flex align-items-center">
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import store from './../../mixins/store.mixin';
import currentJob from './../../mixins/current_job.mixin';
import StudioManager from '../../manager/StudioManager';
import user from '../../mixins/user.mixin';
import {EventBus} from '../../events/event.bus';
import {EVENT_JOB_START} from '../../events/job.events';
import Analytics from "../../util/Analytics";

export default {
  name: 'Topbar',
  props: {},
  components: {},
  mixins: [store, currentJob, user],
  data() {
    return {};
  },
  mounted: function () {
  },
  created: function () {
    EventBus.$on(EVENT_JOB_START, () => {
      this.scrollToNewTab();
    });
  },
  methods: {
    createJob() {
      StudioManager.newJob(this.id);
      Analytics.send('creator_studio_action', `topnav_new_job`, this.currentJob);
    },
    openJob(id) {
      StudioManager.openJob(id, this.id);
      Analytics.send('creator_studio_action', `topnav_open_job`, this.currentJob);
    },
    onWheel: function (e) {
      if (e.deltaY > 0) {
        this.scrollRight();
      } else {
        this.scrollLeft();
      }
    },
    remove(id, index, isActive) {
      this.getJobById(id).isOpen = false;
      Analytics.send('creator_studio_action', `topnav_remove`, this.currentJob);

      if (isActive) {
        const newTabIndex = index > 0 ? index - 1 : index + 1;

        if (this.openJobs.length > 1) {
          StudioManager.openJob(this.openJobs[newTabIndex]?.id);
        } else {
          StudioManager.openWelcomeJob();
        }
      }
    },
    scrollLeft() {
      let content = document.getElementById('files-bar-container'),
          scrollStep = 200;

      let sl = content.scrollLeft;

      if (sl - scrollStep <= 0) {
        content.scrollTo(0, 0);
      } else {
        content.scrollTo(sl - scrollStep, 0);
      }
    },
    scrollRight() {
      let content = document.getElementById('files-bar-container'),
          scrollStep = 200;

      let sl = content.scrollLeft,
          cw = content.scrollWidth;

      if (sl + scrollStep >= cw) {
        content.scrollTo(cw, 0);
      } else {
        content.scrollTo(sl + scrollStep, 0);
      }
    },
    scrollToNewTab() {
      const tabElement = document.querySelector('.nav-item-file.nav-item-file-empty');
      const container = document.getElementById('files-bar-container');

      if (tabElement && container) {
        tabElement.scrollIntoView({
          behavior: 'smooth',
          container: container,
        });
      }
    },
    handleProOrChange() {
      if (this.isFreeUser) {
        EventBus.$emit(EVENT_PAYMENT_UNLOCK_PRO, {});
      }
    },
  },
  computed: {
    openJobs() {
      return this.creatorStudio.jobs.filter(job => job.isOpen === true);
    },
  },
};
</script>
<style lang="scss">
#topbar-container {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

#topbar-right {
  width: 380px;
  padding-left: 20px;
  min-width: 380px;
  border-left: 1px solid #595959;
}

#files-bar-container {
  overflow-x: scroll;
}

.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.nav-item {
  &:hover {
    background: #2c2c2c;
  }
}

.nav-item-file-new {
  width: 100px;
  min-width: 100px !important;
}

.lang-ru .nav-item-file-new {
  min-width: 118px !important;
}

.nav-item-file-empty {
  visibility: hidden;
}

.topbar-scroll-container {
  width: 50px;
  border-left: 1px solid #595959;
  display: flex;
  align-items: center;
  gap: 5px;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: #2c2c2c;
  }
}
</style>
